import { useRouter } from 'next/navigation';
import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import styles from './potential-risks.module.less';
import { cn } from '@/lib/utils';
import { useAddAccountModal } from '@/context/AddAccountModalProvider';

export default function PotentialRisks() {
    const { t } = useTranslation();
    const router = useRouter();
    const { openAddAccountModal } = useAddAccountModal();

    // 生成随机分数的辅助函数
    const getRandomScore = (min: number, max: number) => {
        return Math.floor(Math.random() * (max - min + 1)) + min;
    };

    // 使用 useMemo 缓存风险数据，防止重新渲染时随机分数变化
    // action: 移动端统一为 Check Now，PC 端各行文案不同，由 CSS 断点控制显示
    // onAction: 前两行拉起添加账号弹窗（默认预选 OnlyFans），后两行跳转对应落地页
    const risksData = useMemo(() => [
        {
            icon: '/images/potential-risks/icon-impersonation.svg',
            name: t('Fake Accounts & Impersonation'),
            score: getRandomScore(3, 5), // 3-5分随机
            maxScore: 5,
            pcAction: t('Find Fake Accounts'),
            onAction: () => openAddAccountModal?.({ defaultPlatform: 'ONLYFANS', copyVariant: 'shadowban' }),
        },
        {
            icon: '/images/potential-risks/icon-content-theft.svg',
            name: t('Leaked Creator Content'),
            score: getRandomScore(4, 5), // 4-5分随机
            maxScore: 5,
            pcAction: t('Scan for Content Leaks'),
            onAction: () => openAddAccountModal?.({ defaultPlatform: 'ONLYFANS', copyVariant: 'shadowban' }),
        },
        {
            icon: '/images/potential-risks/icon-privacy-leak.svg',
            name: t('Private Content Exposure'),
            score: getRandomScore(2, 5), // 2-5分随机
            maxScore: 5,
            pcAction: t('Check for Privacy Leaks'),
            onAction: () => router.push('/content-monitoring'),
        },
        {
            icon: '/images/potential-risks/icon-image-leak.svg',
            name: t('Unauthorized Photo Reposts'),
            score: getRandomScore(2, 5), // 2-5分随机
            maxScore: 5,
            pcAction: t('Reverse Search a Photo'),
            onAction: () => router.push('/content-monitoring/reverse-photo-search'),
        },
    ], []); // 空依赖数组确保只在组件首次渲染时生成一次

    // 根据分数获取颜色类名
    const getScoreColor = (score: number) => {
        if (score >= 4) return styles.high; // 4-5分：红色
        if (score === 3) return styles.medium; // 3分：黄色
        return styles.low; // 1-2分：绿色
    };

    return (
        <div className={styles.container}>
            <div className={styles.flexContainer}>
                {/* 标题与说明 */}
                <h2 className={styles.title}>
                    {t('Other Online Risks to Check')}
                </h2>
                <p className={styles.subtitle}>
                    {t('Beyond visibility, your identity and content may also be at risk.')}
                </p>

                {/* 风险列表容器 */}
                <div className={styles.riskListContainer}>
                    {/* 表格头部 */}
                    <div className={styles.tableHeader}>
                        <div className={cn(styles.headerCell, styles.parameterCell)}>
                            {t('Parameter')}
                        </div>
                        <div className={cn(styles.headerCell, styles.scoreCell)}>
                            {t('Risk Score')}
                        </div>
                        <div className={cn(styles.headerCell, styles.actionCell)}>
                            {t('Action')}
                        </div>
                    </div>

                    {/* 风险列表 */}
                    <div className={styles.riskList}>
                        {risksData.map((risk, index) => (
                            <div key={index} className={styles.riskItem}>
                                {/* 左侧：图标和名称 */}
                                <div className={styles.nameSection}>
                                    <img
                                        src={risk.icon}
                                        alt={risk.name}
                                        className={styles.icon}
                                    />
                                    <span className={styles.name}>
                                        {risk.name}
                                    </span>
                                </div>

                                {/* 中间：分数和进度条（垂直布局） */}
                                <div className={styles.scoreSection}>
                                    <div className={styles.scoreText}>
                                        {risk.score}
                                    </div>
                                    <div className={styles.progressBar}>
                                        <div
                                            className={cn(styles.progressFill, getScoreColor(risk.score))}
                                            style={{ width: `${(risk.score / risk.maxScore) * 100}%` }}
                                        ></div>
                                    </div>
                                </div>

                                {/* 右侧：扫描按钮 */}
                                <div className={styles.actionSection}>
                                    <button
                                        className={styles.scanButton}
                                        onClick={risk.onAction}
                                    >
                                        {/* 两段文案均渲染进 DOM，由断点控制显示，
                                            保证 SSR 与客户端一致、无首屏闪动 */}
                                        <span className={styles.actionMobile}>{t('Check Now')}</span>
                                        <span className={styles.actionDesktop}>{risk.pcAction}</span>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* 底部大按钮：拉起添加账号弹窗（shadowban 文案变体，默认预选 OnlyFans） */}
                    <button
                        className={styles.scanAllButton}
                        onClick={() => openAddAccountModal?.({ defaultPlatform: 'ONLYFANS', copyVariant: 'shadowban' })}
                    >
                        {t('Scan My Account for All Risks')} →
                    </button>
                </div>
            </div>
        </div>
    );
}